/* eslint-disable no-underscore-dangle */
/* eslint-disable no-unused-vars */
/* eslint-disable react/jsx-no-bind */
import React, { useState, useEffect, useRef } from 'react';
import { Auth, API } from 'aws-amplify';
import PropTypes from 'prop-types';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { v4 as uuidv4 } from 'uuid';
import { useLocation } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import {
  Bars3Icon,
  InformationCircleIcon,
  BellIcon,
  TrashIcon,
  CheckIcon,
  ExclamationTriangleIcon,
  ExclamationCircleIcon,
  MegaphoneIcon,
  ChatBubbleLeftEllipsisIcon,
  XMarkIcon,
} from '@heroicons/react/24/outline';
import ProjectPicker from './utils/ProjectPicker';
import RegionSelector from './utils/RegionSelector';
import Profile from './Profile';
import GraphqlService from '../service/graphqlService';
import LoadingCard from './utils/LoadingCard';
import { useSnackbar } from '../providers/SnackbarContext';
import { onNewNotificationItem } from '../config/graphql';
import getCurrentAuthenticatedUserId from '../utils/getCurrentAuthenticatedUserId';

function Header({ handleDrawer, handleOpenChangeLogModal, projects }) {
  const [username, setUsername] = useState('Welcome');
  // The backend rejects notification queries for anyone but the caller, so wait for the real id.
  const hasUserIdentifier = !!username && username !== 'Welcome';
  const [userToken, setUserToken] = useState(undefined);
  const [notificationMenuOpen, setNotificationMenuOpen] = useState(false);
  const [animatingNotification, setAnimatingNotification] = useState({ id: null, stage: null }); // Added for delete animation
  const notificationButtonRef = useRef(null);
  const notificationMenuRef = useRef(null);

  const queryClient = useQueryClient();

  const location = useLocation();
  const hideOnRoutes = [
    '/platformHealth',
    '/portalConfiguration',
    '/portalInfo',
    '/notifications',
    '/listCognitoUsers',
    '/support',
    '/reports',
    '/fineGrainedPermissions',
    '/MQTTAnalyticsDashboard',
    '/APIAnalyticsDashboard',
    '/userManagement',
    '/credentialsManagement',
    '/statistics/deploymentDetails',
    '/statistics/deploymentStatus'
  ];

  // Routes where the ProjectPicker is multiselect
  const multiSelectProjectPickerRoutes = [
    '/dashboard-by-project',
    // Add more routes here in the future
  ];

  // Routes where the ProjectPicker shows as a read-only label (selected project disabled)
  const readOnlyProjectPickerRoutes = [
    // Add routes here where you want to show the selected project but prevent selection changes
    // Example: '/myDashboard'
  ];

  const shouldShowProjectPicker = !hideOnRoutes.some((route) =>
    location.pathname.startsWith(route)
  );

  // Determine the ProjectPicker mode
  const projectPickerMode = React.useMemo(() => {
    if (multiSelectProjectPickerRoutes.some((route) =>
      location.pathname.startsWith(route)
    )) {
      return 'multiSelect';
    }
    // deviceOverview with parameters should be read-only
    if (location.pathname.startsWith('/statistics/deviceOverview/') && 
        location.pathname !== '/statistics/deviceOverview') {
      return 'readOnly';
    }
    if (readOnlyProjectPickerRoutes.some((route) =>
      location.pathname.startsWith(route)
    )) {
      return 'readOnly';
    }
    return 'single';
  }, [location.pathname, multiSelectProjectPickerRoutes, readOnlyProjectPickerRoutes]);

  const openSnackBar = useSnackbar();

  useEffect(() => {
    async function checkUser() {
      const authUser = await Auth.currentAuthenticatedUser();
      const userIdentifier = await getCurrentAuthenticatedUserId()
        || 'Welcome';
      setUsername(userIdentifier);
      setUserToken(authUser.signInUserSession.idToken.jwtToken);

      // Guardar el usuario y token en localStorage para usarlo en otras partes del código
      localStorage.setItem('username', userIdentifier);
    }

    checkUser();
  }, []);

  useEffect(() => {
    if (!userToken || !username) {
      return undefined;
    }

    const subscribeToNotifications = async () => {
      try {
        if (!userToken) {
          return null;
        }
        const subscription = API.graphql({
          query: onNewNotificationItem,
          authMode: 'AMAZON_COGNITO_USER_POOLS',
          variables: {
            authToken: Auth.user.attributes?.sub || Auth.user.signInUserSession?.accessToken?.payload?.sub,
          },
        }).subscribe({
          next: (response) => {
            if (response.value.data.onNewNotificationItem.notification.user !== username) {
              return;
            }
            queryClient.invalidateQueries(['notifications']);
            queryClient.invalidateQueries(['unreadNotificationsCount']);
            openSnackBar('New notification received', 'info');
          },
          error: (error) => {
            console.log(`Error subscribing to notifications: ${error}`, 'error');
          },
        });
        return () => subscription.unsubscribe();
      } catch (error) {
        console.log(`Error setting up subscription: ${error}`, 'error');
        return null;
      }
    };

    const unsubscribePromise = subscribeToNotifications();
    return () => {
      if (unsubscribePromise && typeof unsubscribePromise.then === 'function') {
        unsubscribePromise.then(sub => sub && sub());
      } else if (unsubscribePromise) {
        unsubscribePromise();
      }
    };
  }, [userToken, username, queryClient, openSnackBar]);

  useEffect(() => {
    if (!shouldShowProjectPicker && projects?.length > 0) {
      // Only set default project if there's no project selected yet
      const currentProject = localStorage.getItem('project');
      if (!currentProject || currentProject === 'undefined' || currentProject === 'null') {
        const defaultProject = projects[0];
        localStorage.setItem('project', JSON.stringify(defaultProject));
      }
    }
  }, [shouldShowProjectPicker, projects]);

  // Close notification menu when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        notificationMenuOpen &&
        notificationMenuRef.current &&
        !notificationMenuRef.current.contains(event.target) &&
        notificationButtonRef.current &&
        !notificationButtonRef.current.contains(event.target)
      ) {
        setNotificationMenuOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [notificationMenuOpen]);

  const getNotifications = async () => {
    if (!username) {
      return [];
    }
    const result = await GraphqlService.getNotifications({
      user: username,
    });
    return result || [];
  };

  const {
    data: notificationsData,
    isLoading: notificationsLoading,
  } = useQuery(['notifications', username], getNotifications, {
    cacheTime: 0,
    enabled: hasUserIdentifier,
    select: (data) => data?.sort((a, b) => new Date(b.timestamp) - new Date(a.timestamp)),
  });

  const getUnreadNotificationsCount = async () => GraphqlService.getUnreadNotificationsCount({
    user: username,
  });

  const {
    data: unreadCountData,
  } = useQuery(['unreadNotificationsCount', username], getUnreadNotificationsCount, {
    cacheTime: 0,
    enabled: hasUserIdentifier,
  });

  const markAsReadMutation = useMutation(
    (variables) => GraphqlService.markAsReadMutation(variables.notificationId),
    {
      onSuccess: () => {
        openSnackBar('Notification read', 'success');
        queryClient.invalidateQueries(['notifications']);
        queryClient.invalidateQueries(['unreadNotificationsCount']);
      },
    },
  );

  const handleMarkAsRead = (notificationId) => {
    markAsReadMutation.mutate({ notificationId });
  };

  const deleteNotificationMutation = useMutation(
    (variables) => GraphqlService.deleteNotification(variables.notificationId),
    {
      onSuccess: () => {
        openSnackBar('Notification deleted', 'success');
        queryClient.invalidateQueries(['notifications']);
        queryClient.invalidateQueries(['unreadNotificationsCount']);
      },
    },
  );

  const handleDeleteNotification = (notificationId) => {
    deleteNotificationMutation.mutate({ notificationId });
  };

  // Animation control for delete
  const listItemVariants = {
    idle: (notification) => ({
      backgroundColor: notification.read ? 'rgba(249, 250, 251, 1)' : 'rgba(239, 246, 255, 1)', // gray-50 or blue-50
      x: 0,
      opacity: 1,
      transition: { duration: 0.3, ease: "easeInOut" }
    }),
    deleting: {
      backgroundColor: "rgba(255, 0, 0, 0.3)", // Semi-transparent red
      x: 20, // Move to the right
      opacity: 0.7, // Fade slightly
      transition: { duration: 0.4, ease: "easeInOut" }
    },
    exit: { // For AnimatePresence when item is removed
      opacity: 0,
      x: -50, // Animate out to the left
      transition: { duration: 0.3 }
    }
  };

  const determineAnimationState = (notification, currentAnimNotification) => {
    if (currentAnimNotification.id === notification.id && currentAnimNotification.stage === 'startDeleteAnimation') {
      return 'deleting';
    }
    return 'idle';
  };

  const startDeleteAnimation = (notificationId) => {
    setAnimatingNotification({ id: notificationId, stage: 'startDeleteAnimation' });
  };

  const handleItemAnimationComplete = (completedNotificationId) => {
    if (animatingNotification.id === completedNotificationId && animatingNotification.stage === 'startDeleteAnimation') {
      handleDeleteNotification(completedNotificationId); // Actual deletion, triggers AnimatePresence exit
      setAnimatingNotification({ id: null, stage: null }); // Reset animation state
    }
  };


  function toggleNotificationMenu() {
    setNotificationMenuOpen((prev) => !prev);
    if (!notificationMenuOpen) { // If opening the menu
      notificationsData?.forEach((notification) => {
        if (!notification.read) {
          handleMarkAsRead(notification.id);
        }
      });
    }
  }

  const detectAndRenderLinks = (text) => {
    const urlRegex = /(https?:\/\/[^\s]+)/g;
    const parts = text.split(urlRegex);

    return parts.map((part) => {
      if (part.match(urlRegex)) {
        return (
          <a href={part} key={uuidv4()} target="_blank" rel="noopener noreferrer" className="text-blue-500 hover:underline">
            here
          </a>
        );
      }
      return part;
    });
  };

  const handleGiveFeedback = () => {
    window._qsie = window._qsie || [];
    window._qsie.push('SendFeedback');
    console.log('Qualtrics API run');
    if (window.QSI) {
      console.log('Qualtrics API run QSI');
      window.QSI.API.unload();
      window.QSI.API.load();
      window.QSI.API.run();
    }
  };

  const renderNotificationIcon = (type) => {
    switch (type) {
      case 'info':
        return <MegaphoneIcon className="h-6 w-6 text-blue-500" />;
      case 'warning':
        return <ExclamationTriangleIcon className="h-6 w-6 text-yellow-500" />;
      case 'error':
        return <ExclamationCircleIcon className="h-6 w-6 text-red-500" />;
      default:
        return <InformationCircleIcon className="h-6 w-6 text-gray-500" />;
    }
  };

  const notificationsListItems = () => {
    if (notificationsLoading) {
      return (
        <li className="p-4">
          <LoadingCard />
        </li>
      );
    }

    if (!notificationsData || notificationsData.length === 0) {
      return (
        <li className="p-4 text-center text-gray-500">
          No notifications
        </li>
      );
    }

    return (
      <AnimatePresence initial={false}>
        {notificationsData?.map((notification) => (
          <motion.li
            key={notification.id}
            custom={notification}
            variants={listItemVariants}
            initial="idle"
            animate={determineAnimationState(notification, animatingNotification)}
            exit="exit"
            layout
            onAnimationComplete={() => handleItemAnimationComplete(notification.id)}
            className="p-3 border-b border-gray-200" // Background color is now handled by variants
          >
            <div className="flex items-start space-x-3">
              <div className="flex-shrink-0">
                {renderNotificationIcon(notification.type)}
              </div>
              <div className="flex-grow">
                <p className="text-sm font-medium text-gray-900">{notification.title}</p>
                <div className="text-sm text-gray-600 break-words">
                  {detectAndRenderLinks(notification.description)}
                </div>
                <p className="text-xs text-gray-400 mt-1">
                  {new Date(notification.timestamp).toLocaleString()}
                </p>
              </div>
              <div className="flex-shrink-0 flex flex-col space-y-1 items-center">
                {!notification.read && (
                  <button
                    type="button"
                    title="Mark as read"
                    onClick={() => handleMarkAsRead(notification.id)}
                    className="p-1 text-gray-400 hover:text-green-500"
                  >
                    <CheckIcon className="h-5 w-5" />
                  </button>
                )}
                <button
                  type="button"
                  title="Delete"
                  onClick={() => startDeleteAnimation(notification.id)} // Changed to start animation
                  className="p-1 text-gray-400 hover:text-red-500"
                >
                  <TrashIcon className="h-5 w-5" />
                </button>
              </div>
            </div>
          </motion.li>
        ))}
      </AnimatePresence>
    );
  };

  return (
    <>
      <header className="bg-white shadow-md text-black z-50">
        <div className="container mx-auto px-4">
          <div className="flex items-center justify-between h-16">
            <div className="flex items-center">
              <button
                type="button"
                aria-label="Open drawer"
                onClick={handleDrawer}
                className="md:hidden mr-3 p-2 rounded-md text-gray-700 hover:bg-gray-100 focus:outline-none focus:ring-2 focus:ring-inset focus:ring-blue-500"
              >
                <Bars3Icon className="h-6 w-6" />
              </button>
              <div className="flex-grow">
                {shouldShowProjectPicker && (
                  <ProjectPicker
                    projects={projects}
                    mode={projectPickerMode}
                    onDone={() => {
                      // Forzar actualización de dashboards usando un evento global
                      const event = new CustomEvent('projectPickerDone');
                      window.dispatchEvent(event);
                    }}
                  />
                )}
              </div>
              <div className="ml-4 hidden md:block">
                <RegionSelector />
              </div>
            </div>

            <div className="flex items-center space-x-3">
              <div className="relative group">
                <button
                  ref={notificationButtonRef}
                  type="button"
                  onClick={toggleNotificationMenu}
                  className="p-2 rounded-full text-gray-700 hover:bg-gray-100 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
                >
                  <BellIcon className="h-6 w-6" />
                  {unreadCountData > 0 && (
                    <span className="absolute top-0 right-0 block h-4 w-4 transform -translate-y-1/2 translate-x-1/2 rounded-full ring-2 ring-white bg-blue-500 text-white text-xs flex items-center justify-center">
                      {unreadCountData}
                    </span>
                  )}
                </button>
                <span className="absolute top-full left-1/2 transform -translate-x-1/2 mt-2 px-2 py-1 text-xs text-white bg-blue-600 rounded opacity-0 group-hover:opacity-100 transition-opacity duration-200 whitespace-nowrap pointer-events-none z-50">
                  Notifications
                </span>
                <AnimatePresence>
                  {notificationMenuOpen && (
                    <motion.div
                      ref={notificationMenuRef}
                      initial={{ opacity: 0, scale: 0.95, y: -10 }}
                      animate={{ opacity: 1, scale: 1, y: 0 }}
                      exit={{ opacity: 0, scale: 0.95, y: -10 }}
                      transition={{ duration: 0.2 }}
                      className="absolute right-0 mt-2 w-96 max-h-[500px] bg-white rounded-md shadow-lg ring-1 ring-black ring-opacity-5 overflow-hidden z-50"
                    >
                      <div className="flex justify-between items-center px-4 py-3 bg-gray-100 border-b border-gray-200">
                        <h3 className="text-md font-medium text-gray-800">
                          Notifications ({unreadCountData || 0} unread)
                        </h3>
                        <button
                          type="button"
                          onClick={toggleNotificationMenu}
                          className="text-gray-400 hover:text-gray-600"
                        >
                          <XMarkIcon className="h-5 w-5" />
                        </button>
                      </div>
                      <ul className="divide-y divide-gray-200 overflow-y-auto max-h-[400px] scrollbar scrollbar-thin scrollbar-thumb-gray-400 scrollbar-track-gray-100">
                        {notificationsListItems()}
                      </ul>
                    </motion.div>
                  )}
                </AnimatePresence>
              </div>

              <div className="relative group">
                <button
                  type="button"
                  onClick={handleOpenChangeLogModal}
                  className="p-2 rounded-full text-gray-700 hover:bg-gray-100 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
                >
                  <InformationCircleIcon className="h-6 w-6" />
                </button>
                <span className="absolute top-full left-1/2 transform -translate-x-1/2 mt-2 px-2 py-1 text-xs text-white bg-blue-600 rounded opacity-0 group-hover:opacity-100 transition-opacity duration-200 whitespace-nowrap pointer-events-none z-50">
                  What&apos;s New
                </span>
              </div>

              <div className="relative group">
                <button
                  type="button"
                  id="btn-give-feedback"
                  onClick={handleGiveFeedback}
                  className="p-2 rounded-full text-gray-700 hover:bg-gray-100 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
                >
                  <ChatBubbleLeftEllipsisIcon className="h-6 w-6" />
                </button>
                <span className="absolute top-full left-1/2 transform -translate-x-1/2 mt-2 px-2 py-1 text-xs text-white bg-blue-600 rounded opacity-0 group-hover:opacity-100 transition-opacity duration-200 whitespace-nowrap pointer-events-none z-50">
                  Give Feedback
                </span>
              </div>

              <div className="relative group">
                <div className="cursor-pointer">
                  <Profile user={username} />
                </div>
              </div>
            </div>
          </div>
        </div>
      </header>
    </>
  );
}

Header.propTypes = {
  handleDrawer: PropTypes.func.isRequired,
  handleOpenChangeLogModal: PropTypes.func.isRequired,
  projects: PropTypes.arrayOf(PropTypes.object),
};

Header.defaultProps = {
  // undefined while loading, so ProjectPicker can tell "loading" from "no projects".
  projects: undefined,
};

export default Header;
